import {
  ApiError,
  type ApiErrorBody,
  type LoginInput,
  type Paginated,
  type Person,
  type SessionUser,
} from '@fc/core'

export type { Person, SessionUser } from '@fc/core'

/**
 * Base path for the API. In dev, Vite proxies `/api` -> the Elysia server
 * (see vite.config.ts) so the SPA and API stay same-origin and the session
 * cookie is first-party. In production a reverse proxy maps `/api` the same way.
 */
const BASE = '/api'

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
    ...init,
  })

  if (!res.ok) {
    let body: ApiErrorBody | undefined
    try {
      body = (await res.json()) as ApiErrorBody
    } catch {
      /* non-JSON error response */
    }
    throw new ApiError(
      res.status,
      body?.error ?? { code: 'http_error', message: `Request failed (${res.status})` },
    )
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

/**
 * Reusable typed fetch helper for per-domain API clients (e.g.
 * `apps/web/src/lib/events-api.ts`). Throws {@link ApiError} on non-2xx.
 */
export { request as apiFetch }

// ---- Auth ----
export const login = (input: LoginInput) =>
  request<SessionUser>('/auth/login', { method: 'POST', body: JSON.stringify(input) })

export const logout = () => request<void>('/auth/logout', { method: 'POST' })

export const me = () => request<SessionUser>('/auth/me')

// ---- People ----
export const listPeople = async (): Promise<Person[]> => {
  const data = await request<Paginated<Person>>('/people')
  return data.items
}

export const createPerson = (input: Omit<Person, 'id' | 'createdAt' | 'campus'>) =>
  request<Person>('/people', { method: 'POST', body: JSON.stringify(input) })
